/**
 * API клиент для взаимодействия с Backend
 * Интегрирован с NetworkManager для управления офлайн-режимом
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.recvi.ru'
const API_VERSION = '/api/v1'

export interface ApiError {
  success: false
  error: {
    message: string
    code: string
  }
}

export class ApiClient {
  private baseUrl: string
  private deviceId: string | null = null
  private deviceSecret: string | null = null

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_BASE_URL
  }

  /**
   * Возвращает базовый URL API
   */
  getBaseUrl(): string {
    return this.baseUrl
  }

  /**
   * Устанавливает credentials устройства
   */
  setDeviceCredentials(deviceId: string, deviceSecret: string): void {
    this.deviceId = deviceId
    this.deviceSecret = deviceSecret
  }

  /**
   * Выполняет запрос к API с retry логикой
   * Интегрирован с NetworkManager для корректной обработки офлайна
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retries: number = 3,
    retryDelay: number = 1000
  ): Promise<T> {
    const url = `${this.baseUrl}${API_VERSION}${endpoint}`
    
    console.log('[ApiClient] 📡 Starting request:', {
      method: options.method || 'GET',
      endpoint,
      fullUrl: url,
      baseUrl: this.baseUrl,
      isOnline: navigator.onLine
    })
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    // Список публичных эндпоинтов, которые НЕ требуют device authentication
    const publicEndpoints = [
      '/tablet/register',
      '/tablet/register/activate',
      '/tablet/register/status/'
    ]
    
    // Проверяем, является ли эндпоинт публичным
    const isPublicEndpoint = publicEndpoints.some(publicPath => endpoint.startsWith(publicPath))

    // Получаем актуальные credentials из store перед каждым запросом
    // Это гарантирует что мы используем свежие данные даже если устройство
    // было зарегистрировано после загрузки приложения
    const { useDeviceStore } = await import('../store/deviceStore')
    const deviceStore = useDeviceStore.getState()
    
    // Используем credentials из store если они есть, иначе из кеша apiClient
    const deviceId = deviceStore.deviceId || this.deviceId
    const deviceSecret = deviceStore.deviceSecret || this.deviceSecret

    // Добавляем заголовки аутентификации устройства ТОЛЬКО для защищенных эндпоинтов
    if (!isPublicEndpoint && deviceId && deviceSecret) {
      headers['X-Device-ID'] = deviceId
      headers['X-Device-Secret'] = deviceSecret
    }

    // Проверяем статус сети через NetworkManager
    const { NetworkManager } = await import('./networkManager')
    const isOnline = NetworkManager.isOnline()

    console.log('[ApiClient] 🌐 Network status:', {
      isOnline,
      navigatorOnline: navigator.onLine,
      hasDeviceCredentials: !!(deviceId && deviceSecret)
    })

    // Если офлайн, не пытаемся делать запрос
    if (!isOnline && !navigator.onLine) {
      console.error('[ApiClient] ❌ Offline - cannot make request')
      const error = new Error('Нет подключения к интернету')
      error.name = 'NetworkError'
      throw error
    }

    let lastError: Error | null = null

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Проверяем онлайн статус перед запросом
        if (!navigator.onLine && attempt < retries) {
          await this.waitForOnline(retryDelay * (attempt + 1))
          continue
        }

        console.log('[ApiClient] 🚀 Sending fetch request:', {
          url,
          method: options.method,
          headers: { ...headers, 'X-Device-Secret': headers['X-Device-Secret'] ? '***' : undefined },
          hasBody: !!options.body,
          attempt: attempt + 1,
          maxAttempts: retries + 1
        })

        // Создаем AbortController для таймаута (совместимо со старыми браузерами)
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 30000) // Таймаут 30 секунд

        try {
          const response = await fetch(url, {
            ...options,
            headers,
            signal: controller.signal,
          })
          
          clearTimeout(timeoutId)

          console.log('[ApiClient] 📥 Response received:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            headers: Object.fromEntries(response.headers.entries())
          })

          if (!response.ok) {
            // Не повторяем для клиентских ошибок (4xx)
            if (response.status >= 400 && response.status < 500) {
              let errorData: any
              let errorText: string = ''
              
              try {
                // Пытаемся получить текст ответа для логирования
                errorText = await response.clone().text()
                errorData = JSON.parse(errorText)
              } catch {
                // Если не JSON, используем текст или статус
                errorData = { detail: errorText || response.statusText || 'Bad Request' }
              }
              
              // Извлекаем детальное сообщение об ошибке (FastAPI использует разные форматы)
              const errorMessage = 
                errorData.detail || 
                (Array.isArray(errorData.detail) ? errorData.detail.map((e: any) => e.msg || e.message || String(e)).join(', ') : null) ||
                errorData.error?.message || 
                errorData.message || 
                errorData.msg ||
                response.statusText || 
                'API request failed'
              
              // Логируем детали для отладки
              console.error(`API Error ${response.status} ${endpoint}:`, {
                status: response.status,
                statusText: response.statusText,
                errorData,
                errorText: errorText.substring(0, 500), // Первые 500 символов
              })
              
              throw new Error(errorMessage)
            }

            // Повторяем для серверных ошибок (5xx) и сетевых ошибок
            if (attempt < retries) {
              await this.delay(retryDelay * (attempt + 1))
              continue
            }

            const errorData = await response.json().catch(() => ({
              error: { message: response.statusText, code: 'unknown' },
            }))
            throw new Error(errorData.error?.message || 'API request failed')
          }

          const responseData = await response.json()
          console.log('[ApiClient] ✅ Request successful:', responseData)
          return responseData
        } catch (fetchError) {
          clearTimeout(timeoutId)
          throw fetchError
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error))
        
        console.error('[ApiClient] ❌ Request error:', {
          error,
          errorType: typeof error,
          errorName: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : String(error),
          isNetworkError: this.isNetworkError(error),
          isAbortError: error instanceof Error && (error.name === 'AbortError' || error.message.includes('aborted')),
          attempt: attempt + 1,
          willRetry: attempt < retries && this.isNetworkError(error)
        })
        
        // Не повторяем для AbortError (таймаут)
        if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('aborted'))) {
          console.error('[ApiClient] ⏱️ Request timeout (30s) or aborted')
          throw error
        }

        // Если сетевая ошибка, НЕ логируем в console.error
        // NetworkManager сам залогирует в IndexedDB
        if (this.isNetworkError(error)) {
          console.warn('[ApiClient] 🌐 Network error detected')
          // Тихо пробрасываем сетевую ошибку
          if (attempt >= retries) {
            console.error('[ApiClient] ❌ Max retries reached, giving up')
            throw lastError
          }
          console.log(`[ApiClient] 🔄 Retrying in ${retryDelay * (attempt + 1)}ms...`)
          await this.delay(retryDelay * (attempt + 1))
          continue
        }

        // Для остальных ошибок пробрасываем сразу
        throw lastError
      }
    }

    throw lastError || new Error('API request failed after retries')
  }

  /**
   * Проверяет, является ли ошибка сетевой
   */
  private isNetworkError(error: unknown): boolean {
    if (!(error instanceof Error)) return false
    
    // Сетевые ошибки
    return (
      error.name === 'TypeError' ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('network')
    )
  }

  /**
   * Задержка перед повтором
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Ожидает восстановления онлайн статуса
   */
  private waitForOnline(maxWaitMs: number = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (navigator.onLine) {
        resolve()
        return
      }

      const timeout = setTimeout(() => {
        window.removeEventListener('online', onOnline)
        reject(new Error('Timeout waiting for online status'))
      }, maxWaitMs)

      const onOnline = () => {
        clearTimeout(timeout)
        window.removeEventListener('online', onOnline)
        resolve()
      }

      window.addEventListener('online', onOnline)
    })
  }

  /**
   * GET запрос
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  /**
   * POST запрос
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /**
   * PUT запрос
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /**
   * DELETE запрос
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}

export const apiClient = new ApiClient()

